import { DEFAULT_SETTINGS } from "./model";
import type {
  AreaType,
  ExpectCode,
  ExternalSource,
  Gender,
  ModelSettings,
  Office,
  OfficeAnswers,
  Packed,
  PublishedSummary,
  RecallCode,
  Respondent,
  RunSample,
  SnapshotSample,
  StateSample,
  VoteCode,
} from "./types";

/* ── Browser storage ───────────────────────────────
 * Web-only app: pasted sources, model settings, decoded runs and national
 * summaries live in this browser's localStorage. A completed run never
 * changes, so it is fetched once and cached in a compact form (~30 bytes
 * per respondent) keyed by run id. The client view keeps only its own-vote
 * weight, under its own key.
 */

const KEYS = {
  activeSource: "midterms:active-source:v1",
  clientWeight: "midterms:client-weight:v1",
  sample: (runId: string) => `midterms:sample:v1:${runId}`,
  samples: "midterms:samples:v1",
  settings: "midterms:settings:v1",
  sources: "midterms:sources:v1",
  summaries: "midterms:summaries:v1",
  summary: (id: string) => `midterms:summary:v1:${id}`,
};
/** Retired county codes (Connecticut's old counties, Alaska's
    Valdez-Cordova) that only runs cached before the maps drew the current
    ones carry; those runs are decoded again. */
const LEGACY_COUNTIES = new Set([
  "02261",
  "09001",
  "09003",
  "09005",
  "09007",
  "09009",
  "09011",
  "09013",
  "09015",
]);

const MAX_CACHED_RUNS = 150;
const MAX_CACHED_SUMMARIES = 40;
const OFFICE_ORDER: Office[] = ["senate", "governor", "house"];

const storage = (): Storage | null =>
  typeof globalThis.localStorage === "undefined"
    ? null
    : globalThis.localStorage;

const read = <T>(key: string, fallback: T): T => {
  try {
    const raw = storage()?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};

/** Returns false when the browser refused the write (quota). */
const write = (key: string, value: unknown): boolean => {
  try {
    storage()?.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};

/* ── Settings & sources ────────────────────────── */

export const loadSettings = (): ModelSettings => ({
  ...DEFAULT_SETTINGS,
  ...read<Partial<ModelSettings>>(KEYS.settings, {}),
});

export const saveSettings = (settings: ModelSettings) =>
  write(KEYS.settings, settings);

export const loadClientWeight = (): number =>
  read<number>(KEYS.clientWeight, DEFAULT_SETTINGS.ownWeight);

export const saveClientWeight = (ownWeight: number) =>
  write(KEYS.clientWeight, ownWeight);

export const loadSources = (): ExternalSource[] =>
  read<ExternalSource[]>(KEYS.sources, []);

export const saveSources = (sources: ExternalSource[]) =>
  write(KEYS.sources, sources);

/** The compared source; `fallback` until one is picked or hidden. */
export const loadActiveSource = (fallback: string | null): string | null =>
  read<string | null>(KEYS.activeSource, fallback);

export const saveActiveSource = (id: string | null) =>
  write(KEYS.activeSource, id);

/* ── Packed respondents ────────────────────────── */

const pick = <T extends string>(
  char: string,
  allowed: readonly T[]
): T | null =>
  (allowed as readonly string[]).includes(char) ? (char as T) : null;

const digit = (char: string): number | null =>
  /\d/.test(char) ? Number(char) : null;

const charOf = (value: number | string | null | undefined): string =>
  value === null || value === undefined ? "-" : String(value)[0];

const AREA_CODES: Record<AreaType, string> = {
  rural: "r",
  suburban: "s",
  urban: "u",
};
const GENDER_CODES: Record<Gender, string> = {
  female: "f",
  male: "m",
  other: "o",
};

export const pack = (r: Respondent, counties: string[]): Packed => {
  const officeChars = OFFICE_ORDER.map((office) => {
    const answers = r.offices[office];
    return answers
      ? `${charOf(answers.vote)}${charOf(answers.neighbors)}${charOf(answers.expect)}`
      : "---";
  }).join("");
  const code = [
    charOf(r.approval),
    r.area ? AREA_CODES[r.area] : "-",
    r.college === null ? "-" : r.college ? "1" : "0",
    charOf(r.finances),
    r.gender ? GENDER_CODES[r.gender] : "-",
    charOf(r.issue),
    charOf(r.neighbors2024),
    charOf(r.recall),
    charOf(r.turnout),
  ].join("");
  const county = r.county ? counties.indexOf(r.county) : -1;
  return [r.age, county >= 0 ? county : null, code + officeChars];
};

export const unpack = (
  [age, county, code]: Packed,
  counties: string[]
): Respondent => {
  const offices: Partial<Record<Office, OfficeAnswers>> = {};
  OFFICE_ORDER.forEach((office, i) => {
    const chunk = code.slice(9 + i * 3, 12 + i * 3);
    if (chunk === "---" || chunk.length < 3) return;
    offices[office] = {
      expect: pick<ExpectCode>(chunk[2], ["L", "R", "U"]),
      neighbors: digit(chunk[1]),
      vote: pick<VoteCode>(chunk[0], ["L", "N", "O", "R", "U"]),
    };
  });
  const area = (Object.keys(AREA_CODES) as AreaType[]).find(
    (k) => AREA_CODES[k] === code[1]
  );
  const gender = (Object.keys(GENDER_CODES) as Gender[]).find(
    (k) => GENDER_CODES[k] === code[4]
  );
  return {
    age,
    approval: digit(code[0]),
    area: area ?? null,
    college: code[2] === "-" ? null : code[2] === "1",
    county: county === null ? null : (counties[county] ?? null),
    finances: digit(code[3]),
    gender: gender ?? null,
    issue: digit(code[5]),
    neighbors2024: digit(code[6]),
    offices,
    recall: pick<RecallCode>(code[7], ["H", "N", "O", "T", "X"]),
    turnout: digit(code[8]),
  };
};

/** The compact form shared by the run cache and the published snapshot;
    the run identity is left out. */
export const packSample = (sample: StateSample): SnapshotSample => {
  const counties = [
    ...new Set(sample.respondents.flatMap((r) => (r.county ? [r.county] : []))),
  ];
  return {
    completedAt: sample.completedAt,
    counties,
    countyNames: sample.countyNames,
    offices: sample.offices,
    packed: sample.respondents.map((r) => pack(r, counties)),
    state: sample.state,
    verbatims: sample.verbatims,
    wave: sample.wave,
  };
};

export const unpackSample = ({
  counties,
  packed,
  ...rest
}: SnapshotSample): StateSample => ({
  ...rest,
  respondents: packed.map((p) => unpack(p, counties)),
});

interface StoredSample extends SnapshotSample {
  /** Missing from runs cached before it was kept: those are decoded again. */
  questionIds?: RunSample["questionIds"];
  runId: string;
  studyId: string;
}

export const readSample = (runId: string): RunSample | null => {
  const stored = read<StoredSample | null>(KEYS.sample(runId), null);
  if (!stored) return null;
  const { counties, packed, questionIds, ...rest } = stored;
  if (!questionIds || counties.some((c) => LEGACY_COUNTIES.has(c))) {
    return null;
  }
  return {
    ...rest,
    questionIds,
    respondents: packed.map((p) => unpack(p, counties)),
  };
};

/** Latest cached sample per state, to paint the map before the API answers. */
export const readCachedSamples = (): Record<string, RunSample> => {
  const latest: Record<string, RunSample> = {};
  for (const runId of read<string[]>(KEYS.samples, [])) {
    const sample = readSample(runId);
    if (!sample) continue;
    const current = latest[sample.state];
    if (
      !current ||
      sample.wave > current.wave ||
      (sample.wave === current.wave && sample.completedAt > current.completedAt)
    ) {
      latest[sample.state] = sample;
    }
  }
  return latest;
};

export const writeSample = (sample: RunSample) => {
  const stored: StoredSample = {
    ...packSample(sample),
    questionIds: sample.questionIds,
    runId: sample.runId,
    studyId: sample.studyId,
  };

  // Keep the index bounded; drop the oldest runs first.
  const index = read<string[]>(KEYS.samples, []).filter(
    (id) => id !== sample.runId
  );
  while (index.length >= MAX_CACHED_RUNS) {
    const evicted = index.shift();
    if (evicted) storage()?.removeItem(KEYS.sample(evicted));
  }
  if (write(KEYS.sample(sample.runId), stored)) {
    write(KEYS.samples, [...index, sample.runId]);
  }
};

/* ── Summaries ─────────────────────────────────── */

interface StoredSummary extends PublishedSummary {
  context: string;
  question: string;
}

/** The summary saved for exactly this question and figures. */
export const readSummary = (
  question: string,
  context: string
): PublishedSummary | null => {
  const stored = read<StoredSummary | null>(
    KEYS.summary(summaryId(question, context)),
    null
  );
  return stored && stored.question === question && stored.context === context
    ? { headline: stored.headline, summary: stored.summary }
    : null;
};

export const writeSummary = (
  question: string,
  context: string,
  summary: PublishedSummary
) => {
  const id = summaryId(question, context);
  const stored: StoredSummary = {
    context,
    headline: summary.headline,
    question,
    summary: summary.summary,
  };

  const index = read<string[]>(KEYS.summaries, []).filter((key) => key !== id);
  while (index.length >= MAX_CACHED_SUMMARIES) {
    const evicted = index.shift();
    if (evicted) storage()?.removeItem(KEYS.summary(evicted));
  }
  if (write(KEYS.summary(id), stored)) {
    write(KEYS.summaries, [...index, id]);
  }
};

/** FNV-1a hash: two texts can share an id, so reads compare the texts. */
const summaryId = (question: string, context: string): string => {
  const text = `${question}\n${context}`;
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
};
